import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { useGetInvoiceQuery, useUpdateInvoiceBillingMutation, useUpdateInvoicePaymentMethodMutation } from '../../api/billingApi';
import { useListProcedureCatalogQuery } from '../../api/procedureCatalogApi';

const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const PAYMENT_METHODS = ['Cash', 'Card', 'Upi', 'BankTransfer', 'Online'];

/**
 * Centralized "Edit Bill" dialog — the ONLY way to edit a bill from the register.
 *
 *  · Invoice number and issue date are IMMUTABLE (read-only here; the API never writes them).
 *  · Procedure lines can be freely added, removed and modified (procedure, sessions, rate).
 *  · Discount and amount paid are editable; totals are recomputed by the server from the
 *    submitted lines, so the register, receipts drawer and PDF always agree.
 */
export default function EditBillModal({ invoice, onClose, onSaved }) {
  // Pull the full invoice (line items) fresh from the server — the register row only carries a summary.
  const { data: full, isFetching } = useGetInvoiceQuery(invoice.id);
  const { data: catalog = [] } = useListProcedureCatalogQuery(true);
  const [saveBilling, { isLoading: saving }] = useUpdateInvoiceBillingMutation();
  const [saveMethod] = useUpdateInvoicePaymentMethodMutation();

  const [items, setItems] = useState(null); // [{description, quantity, unitPrice}]
  const [discountAmount, setDiscountAmount] = useState('0');
  const [amountPaid, setAmountPaid] = useState('0');
  const [method, setMethod] = useState('');

  // Seed the form once the full invoice arrives.
  useEffect(() => {
    if (!full || items !== null) return;
    const seeded = (full.items?.length ? full.items : [{
      description: full.treatmentSummary || '',
      quantity: full.totalSessions ?? 0,
      unitPrice: full.costPerSession ?? 0,
    }]).map((it) => ({
      description: it.description ?? '',
      quantity: it.quantity ?? 0,
      unitPrice: it.unitPrice ?? 0,
    }));
    setItems(seeded);
    setDiscountAmount(String(full.discountAmount ?? 0));
    setAmountPaid(String(full.amountPaid ?? 0));
    const firstMethod = (full.paymentMethods || '').split(',').map((m) => m.trim()).filter(Boolean)[0]
      || full.payments?.[0]?.method || '';
    setMethod(firstMethod);
  }, [full, items]);

  const multiReceipt = (full?.payments?.length ?? 0) > 1;

  const totals = useMemo(() => {
    const rows = items ?? [];
    const sessions = rows.reduce((s, it) => s + Math.max(0, Math.round(num(it.quantity))), 0);
    const subTotal = rows.reduce((s, it) => s + Math.max(0, Math.round(num(it.quantity))) * Math.max(0, num(it.unitPrice)), 0);
    const disc = Math.min(Math.max(0, num(discountAmount)), subTotal);
    return { sessions, subTotal, disc, net: subTotal - disc, balance: num(amountPaid) - (subTotal - disc) };
  }, [items, discountAmount, amountPaid]);

  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...(prev ?? []), { description: '', quantity: 1, unitPrice: 0 }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    const rows = (items ?? []).filter((it) => it.description.trim() || num(it.quantity) > 0 || num(it.unitPrice) > 0);
    if (rows.length === 0) { toast.error('A bill must keep at least one procedure line.'); return; }
    if (rows.some((it) => !it.description.trim())) { toast.error('Every procedure line needs a name.'); return; }
    try {
      const updated = await saveBilling({
        id: invoice.id,
        items: rows.map((it) => ({
          description: it.description.trim(),
          quantity: Math.max(0, Math.round(num(it.quantity))),
          unitPrice: Math.max(0, num(it.unitPrice)),
        })),
        discountAmount: Math.max(0, num(discountAmount)),
        amountPaid: Math.max(0, num(amountPaid)),
      }).unwrap();
      // Payment method (only when the bill has at most one receipt — multi-receipt bills
      // manage methods in the receipts drawer).
      if (!multiReceipt && method) {
        try { await saveMethod({ id: invoice.id, method }).unwrap(); } catch { /* non-fatal */ }
      }
      toast.success(`Bill ${invoice.invoiceNumber || ''} updated`);
      onSaved?.(updated);
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not save the bill.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <h2>Edit Bill</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {/* ── Immutable header ── */}
          <div className="card card-body" style={{ marginBottom: 14, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <span title="Invoice number cannot be changed" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Lock size={13} style={{ color: 'var(--text-muted)' }} />
              <span className="td-muted" style={{ fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Invoice #</span>
              <strong className="td-mono">{invoice.invoiceNumber || '—'}</strong>
            </span>
            <span title="Issue date cannot be changed" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Lock size={13} style={{ color: 'var(--text-muted)' }} />
              <span className="td-muted" style={{ fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issued</span>
              <strong>{fmtDate(invoice.issuedAt)}</strong>
            </span>
            <span style={{ marginLeft: 'auto' }} className="td-muted">
              {invoice.patientName} {invoice.patientCode ? `· ${invoice.patientCode}` : ''}
            </span>
          </div>

          {isFetching && items === null && <div className="skeleton" style={{ height: 60 }} />}

          {items !== null && (
            <>
              {/* ── Procedure lines (add / remove / modify) ── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label className="form-label" style={{ margin: 0 }}>Procedures / treatments</label>
                <button type="button" className="btn btn-outline btn-sm" onClick={addItem}><Plus size={13} /> Add line</button>
              </div>
              {/* Free-text procedure names with catalog suggestions — works even when the catalog
                  is empty and never blanks a legacy name that predates the catalog. */}
              <datalist id="edit-bill-procedures">
                {catalog.map((c) => <option key={c.id} value={c.name} />)}
              </datalist>
              <div className="tbl-wrap" style={{ marginBottom: 12 }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 220 }}>Procedure</th>
                      <th style={{ width: 110, textAlign: 'right' }}>Sessions</th>
                      <th style={{ width: 140, textAlign: 'right' }}>Rate / session</th>
                      <th style={{ width: 120, textAlign: 'right' }}>Line total</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx}>
                        <td>
                          <input className="form-control" list="edit-bill-procedures"
                            placeholder="Procedure / treatment name"
                            value={it.description}
                            onChange={(e) => setItem(idx, { description: e.target.value })} />
                        </td>
                        <td>
                          <input className="form-control" type="number" min="0" step="1" style={{ textAlign: 'right' }}
                            value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} />
                        </td>
                        <td>
                          <input className="form-control" type="number" min="0" step="any" style={{ textAlign: 'right' }}
                            value={it.unitPrice} onChange={(e) => setItem(idx, { unitPrice: e.target.value })} />
                        </td>
                        <td style={{ textAlign: 'right' }} className="td-bold">
                          {fmt(Math.max(0, Math.round(num(it.quantity))) * Math.max(0, num(it.unitPrice)))}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button type="button" className="btn btn-ghost btn-icon btn-sm" title="Remove line"
                            disabled={items.length <= 1}
                            onClick={() => removeItem(idx)}>
                            <Trash2 size={14} color={items.length <= 1 ? undefined : 'var(--red-500)'} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Money ── */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Discount (₹)</label>
                  <input className="form-control" type="number" min="0" step="any"
                    value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount paid (₹)</label>
                  <input className="form-control" type="number" min="0" step="any"
                    value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment method</label>
                  {multiReceipt ? (
                    <div className="td-muted" style={{ paddingTop: 8, fontSize: '0.8rem' }}>
                      Multiple receipts — manage in the receipts drawer.
                    </div>
                  ) : (
                    <select className="form-control" value={method} onChange={(e) => setMethod(e.target.value)}>
                      <option value="">—</option>
                      {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m === 'Upi' ? 'UPI' : m === 'BankTransfer' ? 'Bank Transfer' : m}</option>)}
                    </select>
                  )}
                </div>
              </div>

              {/* ── Live totals ── */}
              <div className="card card-body" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: '0.85rem', fontWeight: 600 }}>
                <span>Sessions: <strong>{totals.sessions}</strong></span>
                <span>Total cost: <strong>{fmt(totals.subTotal)}</strong></span>
                <span>Discount: <strong>{fmt(totals.disc)}</strong></span>
                <span>Invoiced: <strong>{fmt(totals.net)}</strong></span>
                <span style={{ color: totals.balance < 0 ? 'var(--red-700)' : 'var(--green-700)' }}>
                  Balance: <strong>{totals.balance > 0 ? '+' : ''}{fmt(totals.balance)}</strong>
                </span>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || items === null}>
            {saving ? 'Saving…' : 'Save bill'}
          </button>
        </div>
      </form>
    </div>
  );
}
